import React, { useEffect, useState, useMemo } from "react";
import DateRangeSelector from "../Common/DateRangeSelector";
import { useSelector } from "react-redux";
import { getAllClientsSelector } from "../../store/clientSlice";
import { getAllProductSelector } from "../../store/productSlice";
import {
  getAllInvoiceSelector,
  getTotalDueAmount,
  getTotalCollectedAmount,
  getTotalDueAmountByDateRange,
  getTotalCollectedAmountByDateRange,
} from "../../store/invoiceSlice";
import NumberFormat from "react-number-format";
import useSubscriptionLimits from "../../hooks/useSubscriptionLimits";

import userService from "../../services/userService";

function DashboardWidgets() {
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  
  const clients = useSelector(getAllClientsSelector) || [];
  const products = useSelector(getAllProductSelector) || [];
  const allInvoices = useSelector(getAllInvoiceSelector) || [];
  
  // Use date-filtered selectors when date range is set, otherwise use all-time selectors
  const totalDueAmount = useSelector(
    dateRange.startDate && dateRange.endDate 
      ? getTotalDueAmountByDateRange(dateRange.startDate, dateRange.endDate)
      : getTotalDueAmount
  ) || 0;
  
  const totalCollectedAmount = useSelector(
    dateRange.startDate && dateRange.endDate 
      ? getTotalCollectedAmountByDateRange(dateRange.startDate, dateRange.endDate)
      : getTotalCollectedAmount
  ) || 0;
  
  const { getCurrentPlan, usageCounts, loadUsageCounts, refreshUsageCounts } = useSubscriptionLimits();

  
  const user = userService.getCurrentUser();
  const currentPlan = getCurrentPlan();
  
  // Get current usage counts from Supabase (real-time)
  const exportCount = usageCounts.invoice_exports || 0;
  
  // Listen for export events and refresh immediately
  useEffect(() => {
    const handleExportComplete = (event) => {
      console.log('📊 Dashboard received export event, refreshing counts...');
      refreshUsageCounts(); // Force refresh when export happens
    };

    // Listen for export completion events
    window.addEventListener('invoiceExportComplete', handleExportComplete);
    
    // Initial load
    loadUsageCounts();
    
    return () => {
      window.removeEventListener('invoiceExportComplete', handleExportComplete);
    };
  }, [loadUsageCounts, refreshUsageCounts]);
  
  // Refresh when user changes (login/logout)
  useEffect(() => {
    if (user) {
      refreshUsageCounts(); // Force fresh data on user change
    }
  }, [user, refreshUsageCounts]);

  // Handle date range changes
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange({ startDate, endDate });
  };

  // Get filtered invoice count for the selected date range
  const getFilteredInvoiceCount = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      return allInvoices.length;
    }
    
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    
    return allInvoices.filter(invoice => {
      if (!invoice.createdDate) return false;
      const invoiceDate = new Date(invoice.createdDate);
      return invoiceDate >= start && invoiceDate <= end;
    }).length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInvoices.length, dateRange.startDate, dateRange.endDate]);

  // Format date range for display
  const getDateRangeDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      return 'All Time';
    }
    
    const start = dateRange.startDate.toLocaleDateString();
    const end = dateRange.endDate.toLocaleDateString();
    
    if (start === end) {
      return start;
    }
    
    return `${start} - ${end}`;
  };



  // Get usage percentage for each resource
  const getUsagePercentage = (current, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((current / limit) * 100, 100);
  };

  const clientsPercentage = getUsagePercentage(clients.length, currentPlan.limitations.clients);
  const productsPercentage = getUsagePercentage(products.length, currentPlan.limitations.products);
  const invoicesPercentage = getUsagePercentage(allInvoices.length, currentPlan.limitations.invoicesSaveExport);
  const exportsPercentage = getUsagePercentage(exportCount, currentPlan.limitations.invoicesSaveExport);

  return (
    <div className="space-y-4">
      {/* Date Range Selector */}
      <DateRangeSelector onDateRangeChange={handleDateRangeChange} />
      
      {/* Date Range Display */}
      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
        <div className="text-sm text-blue-800">
          <span className="font-medium">Showing data for:</span> {getDateRangeDisplay()}
        </div>
      </div>

      {/* Subscription Plan Info */}
      {user && currentPlan.id !== 'business' && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {currentPlan.name} Plan
              </h3>
              <p className="text-sm text-gray-600">
                Current usage limits and restrictions
              </p>
            </div>
          </div>
          
          {/* Usage Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {/* Clients */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Clients</div>
              <div className="text-lg font-bold text-gray-900">
                {clients.length}
                {currentPlan.limitations.clients !== -1 && (
                  <span className="text-sm text-gray-500">/{currentPlan.limitations.clients}</span>
                )}
              </div>
              {currentPlan.limitations.clients !== -1 && (
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className={`h-2 rounded-full ${
                      clientsPercentage >= 100 ? 'bg-red-500' : 
                      clientsPercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${clientsPercentage}%` }}
                  ></div>
                </div>
              )}
            </div>

            {/* Products */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Products</div>
              <div className="text-lg font-bold text-gray-900">
                {products.length}
                {currentPlan.limitations.products !== -1 && (
                  <span className="text-sm text-gray-500">/{currentPlan.limitations.products}</span>
                )}
              </div>
              {currentPlan.limitations.products !== -1 && (
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className={`h-2 rounded-full ${
                      productsPercentage >= 100 ? 'bg-red-500' : 
                      productsPercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${productsPercentage}%` }}
                  ></div>
                </div>
              )}
            </div>

            {/* Invoices */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Invoices</div>
              <div className="text-lg font-bold text-gray-900">
                {allInvoices.length}
                {currentPlan.limitations.invoicesSaveExport !== -1 && (
                  <span className="text-sm text-gray-500">/{currentPlan.limitations.invoicesSaveExport}</span>
                )}
              </div>
              {currentPlan.limitations.invoicesSaveExport !== -1 && (
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className={`h-2 rounded-full ${
                      invoicesPercentage >= 100 ? 'bg-red-500' : 
                      invoicesPercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${invoicesPercentage}%` }}
                  ></div>
                </div>
              )}
            </div>

            {/* Exports */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Exports</div>
              <div className="text-lg font-bold text-gray-900">
                {exportCount}
                {currentPlan.limitations.invoicesSaveExport !== -1 && (
                  <span className="text-sm text-gray-500">/{currentPlan.limitations.invoicesSaveExport}</span>
                )}
              </div>
              {currentPlan.limitations.invoicesSaveExport !== -1 && (
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className={`h-2 rounded-full ${
                      exportsPercentage >= 100 ? 'bg-red-500' : 
                      exportsPercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${exportsPercentage}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Original widgets */}
      <div className="flex flex-wrap">
        <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
          <div className="bg-white rounded-xl px-3 py-3 h-32">
            <div className="flex flex-col h-full justify-center">
              <h5 className="text-gray-600 font-bold text-sm">
                Total Clients
              </h5>
              <h1 className="text-gray-700 font-bold text-xl">
                {clients.length}
              </h1>
              <div className="h-4">
                <p className="text-xs text-gray-500 invisible">placeholder</p>
              </div>
            </div>
          </div>
        </div>


        <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
          <div className="bg-white rounded-xl px-3 py-3 h-32">
            <div className="flex flex-col h-full justify-center">
              <h5 className="text-gray-600 font-bold text-sm">Total Invoices</h5>
              <h1 className="text-gray-700 font-bold text-xl">
                {getFilteredInvoiceCount}
              </h1>
              <div className="h-4">
                {dateRange.startDate && dateRange.endDate ? (
                  <p className="text-xs text-gray-500">in selected period</p>
                ) : (
                  <p className="text-xs text-gray-500 invisible">placeholder</p>
                )}
              </div>
            </div>
          </div>
        </div>


        <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
          <div className="bg-white rounded-xl px-3 py-3 h-32">
            <div className="flex flex-col h-full justify-center">
              <h5 className="text-gray-600 font-bold text-sm">Due</h5>
              <h1 className="text-red-600 font-bold text-xl">
                <NumberFormat
                  value={totalDueAmount}
                  className=""
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"₹"}
                  renderText={(value, props) => <span {...props}>{value}</span>}
                />
              </h1>
              <div className="h-4">
                {dateRange.startDate && dateRange.endDate ? (
                  <p className="text-xs text-gray-500">in selected period</p>
                ) : (
                  <p className="text-xs text-gray-500 invisible">placeholder</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
          <div className="bg-white rounded-xl px-3 py-3 h-32">
            <div className="flex flex-col h-full justify-center">
              <h5 className="text-gray-600 font-bold text-sm">Collected</h5>
              <h1 className="text-green-600 font-bold text-xl">
                <NumberFormat
                  value={totalCollectedAmount}
                  className=""
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"₹"}
                  renderText={(value, props) => <span {...props}>{value}</span>}
                />
              </h1>
              <div className="h-4">
                {dateRange.startDate && dateRange.endDate ? (
                  <p className="text-xs text-gray-500">in selected period</p>
                ) : (
                  <p className="text-xs text-gray-500 invisible">placeholder</p>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default React.memo(DashboardWidgets);
